import type { WorldTheme } from '../flags/types';
import {
  drawApartment,
  drawBench,
  drawBrownstone,
  drawBush,
  drawCubicle,
  drawDeskWood,
  drawFireHydrant,
  drawFlowerRed,
  drawFlowerWhite,
  drawFlowerYellow,
  drawMossyRock,
  drawOakTree,
  drawOfficeTower,
  drawParkedCar,
  drawPineTree,
  drawPlanter,
  drawRock,
  drawShop,
  drawSkyscraper,
  drawStreetlamp,
  drawStump,
  drawTallGrass,
  drawTrafficCone,
  drawTrashBin,
  type DecorVariant
} from './decor';

export interface ThemeData {
  ground: number;
  groundPatches: GroundPatchSpec[];
  bg: number;
  decorVariants: DecorVariant[];
  decorCount: number;
}

export interface GroundPatchSpec {
  fill: number;
  alpha: number;
  count: number;
  minSize: number;
  maxSize: number;
  shape: 'circle' | 'rect';
}

export const themes: Record<WorldTheme, ThemeData> = {
  outdoors: {
    ground: 0x6b9b3f,
    bg: 0x87ceeb,
    groundPatches: [
      { fill: 0x4f7a2c, alpha: 0.5, count: 220, minSize: 10, maxSize: 22, shape: 'circle' },
      { fill: 0x84b54f, alpha: 0.45, count: 140, minSize: 8, maxSize: 16, shape: 'circle' },
      { fill: 0xa0894a, alpha: 0.35, count: 30, minSize: 16, maxSize: 32, shape: 'circle' }
    ],
    decorCount: 110,
    decorVariants: [
      {
        key: 'outdoor-oak',
        width: 64,
        height: 76,
        weight: 4,
        scaleMin: 0.9,
        scaleMax: 1.15,
        draw: drawOakTree
      },
      {
        key: 'outdoor-pine',
        width: 40,
        height: 72,
        weight: 3,
        scaleMin: 0.85,
        scaleMax: 1.1,
        draw: drawPineTree
      },
      {
        key: 'outdoor-bush',
        width: 32,
        height: 22,
        weight: 5,
        scaleMin: 0.8,
        scaleMax: 1.2,
        draw: drawBush
      },
      {
        key: 'outdoor-flower-red',
        width: 14,
        height: 18,
        weight: 4,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawFlowerRed
      },
      {
        key: 'outdoor-flower-yellow',
        width: 14,
        height: 18,
        weight: 4,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawFlowerYellow
      },
      {
        key: 'outdoor-flower-white',
        width: 14,
        height: 18,
        weight: 3,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawFlowerWhite
      },
      {
        key: 'outdoor-rock',
        width: 24,
        height: 18,
        weight: 2,
        scaleMin: 0.7,
        scaleMax: 1.5,
        draw: drawRock
      },
      {
        key: 'outdoor-mossy-rock',
        width: 24,
        height: 18,
        weight: 1,
        scaleMin: 0.7,
        scaleMax: 1.4,
        draw: drawMossyRock
      },
      {
        key: 'outdoor-stump',
        width: 20,
        height: 16,
        weight: 1,
        scaleMin: 0.85,
        scaleMax: 1.0,
        draw: drawStump
      },
      {
        key: 'outdoor-grass',
        width: 18,
        height: 14,
        weight: 4,
        scaleMin: 0.9,
        scaleMax: 1.3,
        draw: drawTallGrass
      }
    ]
  },
  city: {
    ground: 0x3f3f46,
    bg: 0x6b7d99,
    groundPatches: [
      // Concrete sidewalk slabs
      { fill: 0x9ca3af, alpha: 0.35, count: 180, minSize: 22, maxSize: 44, shape: 'rect' },
      // Sidewalk seams / asphalt cracks (small dark rects)
      { fill: 0x18181b, alpha: 0.45, count: 90, minSize: 4, maxSize: 10, shape: 'rect' },
      // Faded yellow paint
      { fill: 0xeab308, alpha: 0.4, count: 30, minSize: 6, maxSize: 14, shape: 'rect' },
      // Faded white paint
      { fill: 0xe5e5e5, alpha: 0.35, count: 24, minSize: 4, maxSize: 10, shape: 'rect' },
      // Oil stains / puddles
      { fill: 0x09090b, alpha: 0.55, count: 35, minSize: 8, maxSize: 16, shape: 'circle' },
      // Manhole-ish dark disks
      { fill: 0x27272a, alpha: 0.8, count: 8, minSize: 14, maxSize: 18, shape: 'circle' }
    ],
    decorCount: 130,
    decorVariants: [
      {
        key: 'city-skyscraper',
        width: 40,
        height: 88,
        weight: 3,
        scaleMin: 0.9,
        scaleMax: 1.35,
        draw: drawSkyscraper
      },
      {
        key: 'city-office-tower',
        width: 34,
        height: 64,
        weight: 4,
        scaleMin: 0.9,
        scaleMax: 1.25,
        draw: drawOfficeTower
      },
      {
        key: 'city-brownstone',
        width: 34,
        height: 38,
        weight: 4,
        scaleMin: 0.95,
        scaleMax: 1.15,
        draw: drawBrownstone
      },
      {
        key: 'city-apartment',
        width: 36,
        height: 48,
        weight: 3,
        scaleMin: 0.95,
        scaleMax: 1.2,
        draw: drawApartment
      },
      {
        key: 'city-shop',
        width: 38,
        height: 30,
        weight: 3,
        scaleMin: 0.95,
        scaleMax: 1.15,
        draw: drawShop
      },
      {
        key: 'city-streetlamp',
        width: 12,
        height: 42,
        weight: 5,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawStreetlamp
      },
      {
        key: 'city-car',
        width: 36,
        height: 20,
        weight: 4,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawParkedCar
      },
      {
        key: 'city-bench',
        width: 28,
        height: 16,
        weight: 3,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawBench
      },
      {
        key: 'city-planter',
        width: 16,
        height: 16,
        weight: 4,
        scaleMin: 0.9,
        scaleMax: 1.15,
        draw: drawPlanter
      },
      {
        key: 'city-trash',
        width: 14,
        height: 18,
        weight: 3,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawTrashBin
      },
      {
        key: 'city-hydrant',
        width: 12,
        height: 18,
        weight: 2,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawFireHydrant
      },
      {
        key: 'city-cone',
        width: 12,
        height: 14,
        weight: 2,
        scaleMin: 0.9,
        scaleMax: 1.15,
        draw: drawTrafficCone
      }
    ]
  },
  office: {
    ground: 0xc7a982,
    bg: 0xf3e9d2,
    groundPatches: [
      { fill: 0xa48962, alpha: 0.45, count: 120, minSize: 24, maxSize: 48, shape: 'rect' },
      { fill: 0xdcc499, alpha: 0.35, count: 60, minSize: 8, maxSize: 16, shape: 'rect' }
    ],
    decorCount: 80,
    decorVariants: [
      {
        key: 'office-desk',
        width: 32,
        height: 22,
        weight: 4,
        scaleMin: 0.9,
        scaleMax: 1.15,
        draw: drawDeskWood
      },
      {
        key: 'office-cubicle',
        width: 26,
        height: 26,
        weight: 3,
        scaleMin: 0.9,
        scaleMax: 1.1,
        draw: drawCubicle
      }
    ]
  }
};

export interface DeskStyle {
  fill: number;
  edge: number;
  label: string;
}

export const deskStyles = {
  office: { fill: 0xb45309, edge: 0x713f12, label: 'OFFICE' },
  compliance: { fill: 0x6d28d9, edge: 0x3b1782, label: 'COMPLIANCE' },
  cash: { fill: 0x16a34a, edge: 0x064e3b, label: 'CASH OUT' }
} as const;
